import { useCallback, useState } from 'react';
import { toast } from 'react-hot-toast';
import { maxFileSizeBytes } from '@client/new/common/videos/videos.constants';
import { MAX_AUDIO_FILES, MAX_MEDIA_FILES } from '../model/newPost.constants';

export const isAudioFile = (file: File) => file.type.startsWith('audio');

const revokePreview = (previewUrl?: string) => {
  if (previewUrl?.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
};

export type DraftVideo = {
  uuid: string;
  previewUrl?: string;
  previewIsVideo?: boolean;
  previewTimeSec?: number;
};

type MediaDraft = {
  files: File[];
  videos: DraftVideo[];
  mediaCount: number;
  addFiles: (files: File[]) => void;
  removeFile: (file: File) => void;
  addVideo: (uuid: string, preview?: Omit<DraftVideo, 'uuid'>) => void;
  removeVideo: (uuid: string) => void;
  clear: () => void;
};

export const useMediaDraft = (): MediaDraft => {
  const [files, setFiles] = useState<File[]>([]);
  const [videos, setVideos] = useState<DraftVideo[]>([]);

  const mediaCount = files.length + videos.length;

  const addFiles = useCallback(
    (files: File[]) => {
      setFiles((current) => {
        const withinSize = files.filter(
          (file) => file.size <= maxFileSizeBytes,
        );
        if (withinSize.length !== files.length) {
          toast.error('You cannot upload files bigger than 100MB');
        }

        let audioCount = current.filter(isAudioFile).length;
        const withinAudioLimit = withinSize.filter((file) => {
          if (!isAudioFile(file)) return true;
          if (audioCount >= MAX_AUDIO_FILES) return false;
          audioCount += 1;
          return true;
        });
        if (withinAudioLimit.length < withinSize.length) {
          toast.error('You can attach only one audio file');
        }

        const remaining = MAX_MEDIA_FILES - current.length - videos.length;
        const accepted = withinAudioLimit.slice(0, Math.max(0, remaining));
        if (accepted.length < withinAudioLimit.length) {
          toast.error(`You can upload max. ${MAX_MEDIA_FILES} media files`);
        }

        return accepted.length > 0 ? [...current, ...accepted] : current;
      });
    },
    [videos.length],
  );

  const removeFile = useCallback((file: File) => {
    setFiles((current) => current.filter((item) => item !== file));
  }, []);

  const addVideo = useCallback(
    (uuid: string, preview?: Omit<DraftVideo, 'uuid'>) => {
      setVideos((current) => {
        if (current.some((video) => video.uuid === uuid)) {
          revokePreview(preview?.previewUrl);
          return current;
        }
        if (files.length + current.length >= MAX_MEDIA_FILES) {
          toast.error(`You can upload max. ${MAX_MEDIA_FILES} media files`);
          revokePreview(preview?.previewUrl);
          return current;
        }
        return [...current, { uuid, ...preview }];
      });
    },
    [files.length],
  );

  const removeVideo = useCallback((uuid: string) => {
    setVideos((current) => {
      revokePreview(current.find((video) => video.uuid === uuid)?.previewUrl);
      return current.filter((video) => video.uuid !== uuid);
    });
  }, []);

  const clear = useCallback(() => {
    setFiles([]);
    setVideos((current) => {
      current.forEach((video) => revokePreview(video.previewUrl));
      return [];
    });
  }, []);

  return {
    files,
    videos,
    mediaCount,
    addFiles,
    removeFile,
    addVideo,
    removeVideo,
    clear,
  };
};
